/* iter460 — Lactobot Floating Chat Button.
 *
 * Compact 5-message in-page chat widget for breastfeeding mothers.
 * Wires to POST /api/lactation/chat which uses OpenAI Responses API
 * + the user-provided vector store (vs_6a1f474987088191a7c1d08f7bf3e5c9).
 *
 * Educational only — backend system prompt always returns BG, with explicit
 * red-flag escalation if mastitis symptoms suggest medical urgency.
 */
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import { Bot, Send, X, Loader2, Sparkles } from "lucide-react";

export default function LactobotFloatingButton({ lang = "bg" }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: lang === "bg"
        ? "Здравей! 👋 Аз съм Лактобот — твоят AI асистент при кърмене. Питай ме за поза, проблеми, хранене, мастит, отбиване — каквото те вълнува."
        : "Hi! 👋 I'm Lactobot — your AI breastfeeding companion. Ask me about positions, problems, nutrition, mastitis, weaning — whatever's on your mind.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const msg = (input || "").trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/lactation/chat`, { message: msg });
      setMessages((m) => [...m, { role: "bot", text: data.response }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || (lang === "bg" ? "Грешка при връзката" : "Connection error");
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="lactobot-fab"
        aria-label="Open Lactobot chat"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-rose-400 to-pink-600 shadow-lg shadow-pink-500/40 hover:scale-105 transition-transform flex items-center justify-center text-white"
      >
        <Bot className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full ring-2 ring-white animate-pulse"></span>
      </button>

      {open && (
        <div
          className="fixed inset-0 sm:inset-auto sm:bottom-24 sm:right-6 z-50 sm:w-[400px] sm:h-[600px] flex flex-col"
          data-testid="lactobot-panel"
        >
          {/* Mobile backdrop */}
          <div className="absolute inset-0 bg-black/40 sm:hidden" onClick={() => setOpen(false)} />

          <div
            className={`relative flex-1 sm:flex-none sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden ${
              isDark ? "bg-slate-900 text-slate-100" : "bg-white text-slate-900"
            }`}
          >
            {/* Header */}
            <div className="px-4 py-3 flex items-center gap-3 bg-gradient-to-r from-rose-400 to-pink-600 text-white">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold">AI Лактобот</div>
                <div className="text-[10px] opacity-90">
                  {lang === "bg" ? "Подкрепа при кърмене · educational" : "Breastfeeding support · educational"}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="lactobot-close"
                className="p-1.5 rounded-full hover:bg-white/20"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="lactobot-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-pink-600 text-white"
                        : isDark
                          ? "bg-slate-800 text-slate-100"
                          : "bg-slate-100 text-slate-900"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className={`px-3 py-2 rounded-2xl text-sm flex items-center gap-2 ${
                    isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"
                  }`}>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span className="text-xs">{lang === "bg" ? "Лактобот пише…" : "Lactobot is typing…"}</span>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Input */}
            <div className={`px-3 py-3 border-t ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={lang === "bg" ? "Питай Лактобот…" : "Ask Lactobot…"}
                  rows={1}
                  data-testid="lactobot-input"
                  className={`flex-1 resize-none px-3 py-2 rounded-lg text-sm max-h-24 ${
                    isDark
                      ? "bg-slate-800 text-white placeholder:text-slate-500"
                      : "bg-slate-50 text-slate-900 placeholder:text-slate-400"
                  }`}
                  disabled={sending}
                />
                <button
                  onClick={send}
                  disabled={sending || !input.trim()}
                  data-testid="lactobot-send"
                  className="w-9 h-9 rounded-lg bg-pink-600 hover:bg-pink-700 disabled:bg-slate-400 text-white flex items-center justify-center"
                  aria-label="Send"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <p className={`text-[10px] mt-2 flex items-center gap-1 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                <Sparkles className="w-3 h-3" />
                {lang === "bg"
                  ? "Powered by Nutramilo Lactobot · Educational, not medical advice"
                  : "Powered by Nutramilo Lactobot · Educational, not medical advice"}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
